import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import Typography from "@mui/material/Typography"
import { Box, IconButton, useTheme } from "@mui/material"
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import { appActions } from "redux/app/slice/app"
import {
  convertDates,
  formatDate,
  formatMonthYear,
  isFutureDate,
  isInHoverRange,
  isInRange
} from "helpers/functions"
import EventIcon from "@mui/icons-material/Event"
import styles from "./calender.module.scss"

const CustomDateRangePicker = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const currentFilters = useSelector((store) => store.app?.filters || {})
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [showCalendar, setShowCalendar] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [hoverDate, setHoverDate] = useState(null)
  const [selectionMode, setSelectionMode] = useState("start")

  useEffect(() => {
    if (currentFilters.date) {
      setStartDate(currentFilters.date)
    } else {
      setStartDate(formatDate(new Date()))
    }
  }, [currentFilters.date])

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDayOfMonth = new Date(year, month, 1).getDay()

    const prevMonthDays = []
    if (firstDayOfMonth > 0) {
      const prevMonth = month === 0 ? 11 : month - 1
      const prevMonthYear = month === 0 ? year - 1 : year
      const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate()

      for (let i = 0; i < firstDayOfMonth; i++) {
        const day = daysInPrevMonth - firstDayOfMonth + i + 1
        const date = new Date(prevMonthYear, prevMonth, day)
        prevMonthDays.push({
          date: formatDate(date),
          day,
          isCurrentMonth: false
        })
      }
    }

    const currentMonthDays = []
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      currentMonthDays.push({
        date: formatDate(date),
        day,
        isCurrentMonth: true
      })
    }

    const nextMonthDays = []
    const totalDaysSoFar = prevMonthDays.length + currentMonthDays.length
    if (totalDaysSoFar < 42) {
      const nextMonth = month === 11 ? 0 : month + 1
      const nextMonthYear = month === 11 ? year + 1 : year

      for (let day = 1; nextMonthDays.length + totalDaysSoFar < 42; day++) {
        const date = new Date(nextMonthYear, nextMonth, day)
        nextMonthDays.push({
          date: formatDate(date),
          day,
          isCurrentMonth: false
        })
      }
    }

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays]
  }

  const handleDateClick = (dateStr) => {
    const clickedDate = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    clickedDate.setHours(0, 0, 0, 0)

    if (clickedDate > today) return

    if (selectionMode === "start") {
      setStartDate(dateStr)
      setEndDate("")
      setSelectionMode("end")
    } else {
      const start = new Date(startDate)
      start.setHours(0, 0, 0, 0)

      if (clickedDate < start) {
        setStartDate(dateStr)
        setEndDate("")
      } else {
        setEndDate(dateStr)
        setSelectionMode("start")
      }
    }
  }

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    const today = new Date()
    const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)

    if (nextMonthDate > today) return

    setCurrentMonth(nextMonthDate)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendar && !event.target.closest(".date-range-picker")) {
        setShowCalendar(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showCalendar])

  return (
    <Box className={`date-range-picker ${styles.container}`}>
      <Typography
        variant="caption5"
        sx={{
          fontSize: 14,
          display: "block",
          color: theme.palette.text.primary
        }}>
        Date
      </Typography>

      <Box className={styles.inputField} onClick={() => setShowCalendar(!showCalendar)}>
        <Box className="d-flex align-items-center">
          <Typography variant="caption3" sx={{ color: theme.palette.secondary.main, opacity: 1 }}>
            {startDate
              ? `${convertDates(new Date(startDate))}${
                  endDate ? ` - ${convertDates(new Date(endDate))}` : ""
                }`
              : "Select date"}
          </Typography>
        </Box>
        <EventIcon />
      </Box>

      {showCalendar && (
        <Box
          className={styles.calenderHeader}
          sx={{ background: theme.palette.background.default }}>
          <Box className="d-flex align-items-center justify-content-between mb-3">
            <IconButton className={styles.navigationButton} onClick={prevMonth}>
              <ArrowBackIosIcon />
            </IconButton>

            <Typography variant="body1Regular" color={theme.palette.text.primary}>
              {formatMonthYear(currentMonth)}
            </Typography>

            <IconButton className={styles.navigationButton} onClick={nextMonth}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          <Box className={styles.weekDays}>
            {weekDays?.map((day, i) => (
              <Typography key={i} variant="body1Regular" color={theme.palette.text.primary}>
                {day}
              </Typography>
            ))}
          </Box>

          <Box className={styles.calenderDays}>
            {generateCalendarDays().map((day, index) => {
              const isSelected = day.date === startDate || day.date === endDate
              const inRange = isInRange(day.date, startDate, endDate)
              const inHoverRange = isInHoverRange(day.date, selectionMode, startDate, hoverDate)
              const rangeDay = inRange || inHoverRange

              return (
                <Box
                  key={index}
                  className={styles.day}
                  onClick={() => {
                    if (!isFutureDate(day.date)) handleDateClick(day.date)
                  }}
                  onMouseEnter={() => {
                    if (!isFutureDate(day.date) && selectionMode === "end") {
                      setHoverDate(day.date)
                    }
                  }}
                  style={{
                    color: isFutureDate(day.date)
                      ? theme.palette.text.disabled
                      : day.isCurrentMonth
                      ? isSelected || rangeDay
                        ? theme.palette.secondary.primary
                        : theme.palette.text.primary
                      : theme.palette.text.secondary,
                    backgroundColor: isSelected
                      ? theme.palette.secondary.main
                      : rangeDay
                      ? theme.palette.secondary.main
                      : "transparent",
                    borderRadius: isSelected || rangeDay ? "50%" : "0",
                    cursor: isFutureDate(day.date) ? "not-allowed" : "pointer",
                    pointerEvents: isFutureDate(day.date) ? "none" : "auto"
                  }}>
                  {day.day}
                </Box>
              )
            })}
          </Box>

          <Box className={styles.actionButtonContainer}>
            <button
              onClick={() => {
                const today = formatDate(new Date())
                setStartDate(today)
                setEndDate("")
                setSelectionMode("start")
                setShowCalendar(false)
                dispatch(
                  appActions.setFilters({
                    ...currentFilters,
                    startDate: "",
                    endDate: "",
                    date: today
                  })
                )
              }}
              className={styles.actionButton}>
              Today
            </button>
            <button
              onClick={() => {
                setShowCalendar(false)
                dispatch(
                  appActions.setFilters({
                    ...currentFilters,
                    startDate: startDate,
                    endDate: endDate
                  })
                )
              }}
              className={styles.actionButton}>
              Apply
            </button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CustomDateRangePicker

const weekDays = ["S", "M", "T", "W", "T", "F", "S"]
